import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { can, DISABLED_BTN_CLS, permissionTitle } from "../lib/permissions";
import { ROLES } from "../components/Badges";
import { Plus, Edit2, Trash2, X, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { gate_id: 0, gate_name: "", pass_criteria: "", required_roles: [] };

export default function GateTemplates() {
    const { user } = useAuth();
    const canManage = can(user, "manageTemplates");
    const [gates, setGates] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [error, setError] = useState("");

    const refresh = async () => {
        try {
            const data = await api.get("/gate-templates").then((r) => r.data);
            setGates(data);
        } catch (err) {
            const status = err?.response?.status;
            if (status !== 401) {
                console.warn("[GateTemplates] refresh failed:", status, err?.message);
                toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Failed to load gate templates");
            }
        }
    };

    useEffect(() => { refresh(); }, []);

    const openCreate = () => { setForm(EMPTY); setEditing({}); setError(""); };
    const openEdit = (g) => {
        setForm({
            gate_id: g.gate_id,
            gate_name: g.gate_name,
            pass_criteria: g.pass_criteria,
            required_roles: g.required_roles,
        });
        setEditing(g);
        setError("");
    };
    const close = () => setEditing(null);

    const toggleRole = (role) => {
        const has = form.required_roles.includes(role);
        setForm({
            ...form,
            required_roles: has ? form.required_roles.filter((r) => r !== role) : [...form.required_roles, role],
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError("");
        try {
            const payload = { ...form, gate_id: Number(form.gate_id) };
            if (editing && editing.id) {
                await api.patch(`/gate-templates/${editing.id}`, payload);
                toast.success("Gate template updated");
            } else {
                await api.post("/gate-templates", payload);
                toast.success("Gate template created");
            }
            close();
            refresh();
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete(`/gate-templates/${deleteTarget.id}`);
            toast.success("Gate template deleted");
            setDeleteTarget(null);
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div className="space-y-6" data-testid="gate-templates-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="overline">Master Records / Gate Templates</div>
                    <h1 className="heading-display text-4xl mt-1">Gate Templates</h1>
                    <p className="text-snow-dim text-sm mt-2 max-w-2xl">
                        Pass criteria and role authorities for every governance checkpoint (G00 through G10).
                    </p>
                </div>
                <button
                    data-testid="create-gate-btn"
                    onClick={openCreate}
                    disabled={!canManage}
                    title={permissionTitle(user, "manageTemplates")}
                    className={`bg-signal text-jet hover:bg-signal-hover px-5 py-2 transition-colors font-bold uppercase tracking-[0.2em] text-xs flex items-center gap-2 ${!canManage ? DISABLED_BTN_CLS : ""}`}
                >
                    <Plus className="w-3 h-3" /> New Gate Template
                </button>
            </div>

            <div className="border border-hairline bg-jet-elev1">
                <table className="w-full" data-testid="gate-templates-table">
                    <thead>
                        <tr className="border-b border-hairline">
                            <Th className="w-[80px]">ID</Th>
                            <Th>Gate Name</Th>
                            <Th>Pass Criteria</Th>
                            <Th>Required Roles</Th>
                            <Th className="w-[120px]">Actions</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {gates.map((g) => (
                            <tr key={g.id} data-testid={`gate-row-${g.id}`} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                                <Td>
                                    <span className="text-signal font-mono font-bold tabular-nums">G{String(g.gate_id).padStart(2, "0")}</span>
                                </Td>
                                <Td><div className="font-mono text-sm">{g.gate_name}</div></Td>
                                <Td><div className="text-xs text-snow-dim font-mono leading-relaxed max-w-[480px]">{g.pass_criteria}</div></Td>
                                <Td>
                                    <div className="flex flex-wrap gap-1">
                                        {g.required_roles.map((r) => (
                                            <span key={r} className="border border-hairline text-snow-dim text-[10px] px-1.5 py-0.5 uppercase tracking-wider font-mono">{r}</span>
                                        ))}
                                    </div>
                                </Td>
                                <Td>
                                    <div className="flex items-center gap-2">
                                        <button
                                            data-testid={`edit-gate-${g.id}`}
                                            onClick={() => canManage && openEdit(g)}
                                            disabled={!canManage}
                                            title={permissionTitle(user, "manageTemplates")}
                                            className={`p-1.5 border transition-colors ${!canManage ? DISABLED_BTN_CLS : "border-hairline text-snow-dim hover:border-signal hover:text-signal"}`}
                                        >
                                            <Edit2 className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                            data-testid={`delete-gate-${g.id}`}
                                            onClick={() => canManage && setDeleteTarget(g)}
                                            disabled={!canManage}
                                            title={permissionTitle(user, "manageTemplates")}
                                            className={`p-1.5 border transition-colors ${!canManage ? DISABLED_BTN_CLS : "border-hairline text-snow-dim hover:border-red-500 hover:text-red-400"}`}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </Td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing !== null && (
                <Modal title={editing.id ? "Edit Gate Template" : "New Gate Template"} onClose={close}>
                    <form onSubmit={submit} className="space-y-4" data-testid="gate-form">
                        <div className="grid grid-cols-3 gap-4">
                            <Field label="Gate ID (0-10)">
                                <input
                                    data-testid="gate-id-input"
                                    type="number"
                                    min="0"
                                    max="10"
                                    required
                                    value={form.gate_id}
                                    onChange={(e) => setForm({ ...form, gate_id: e.target.value })}
                                    className="cerb-input"
                                />
                            </Field>
                            <div className="col-span-2">
                                <Field label="Gate Name">
                                    <input
                                        data-testid="gate-name-input"
                                        required
                                        value={form.gate_name}
                                        onChange={(e) => setForm({ ...form, gate_name: e.target.value })}
                                        className="cerb-input"
                                    />
                                </Field>
                            </div>
                        </div>
                        <Field label="Pass Criteria">
                            <textarea
                                data-testid="gate-criteria-input"
                                rows={4}
                                value={form.pass_criteria}
                                onChange={(e) => setForm({ ...form, pass_criteria: e.target.value })}
                                className="cerb-input"
                            />
                        </Field>
                        <Field label="Required Roles">
                            <div className="flex flex-wrap gap-2" data-testid="gate-roles-grid">
                                {ROLES.map((r) => {
                                    const selected = form.required_roles.includes(r);
                                    return (
                                        <button
                                            key={r}
                                            type="button"
                                            data-testid={`role-pill-${r.replace(/\s+/g, "-").toLowerCase()}`}
                                            onClick={() => toggleRole(r)}
                                            className={`border px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors flex items-center gap-1.5 ${
                                                selected ? "border-signal text-signal bg-signal/10" : "border-hairline text-snow-dim hover:border-snow"
                                            }`}
                                        >
                                            {selected && <Check className="w-3 h-3" />}
                                            {r}
                                        </button>
                                    );
                                })}
                            </div>
                        </Field>
                        {error && <div className="text-red-400 text-xs font-mono">{error}</div>}
                        <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                            <button type="button" onClick={close} className="cerb-btn-secondary" data-testid="gate-cancel-btn">Cancel</button>
                            <button type="submit" disabled={saving} className="cerb-btn-primary" data-testid="gate-save-btn">
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {deleteTarget && (
                <Modal title="Delete Gate Template" onClose={() => setDeleteTarget(null)}>
                    <div className="space-y-4">
                        <div className="text-sm">Delete <span className="text-signal font-bold">G{String(deleteTarget.gate_id).padStart(2, "0")} — {deleteTarget.gate_name}</span>?</div>
                        <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                            <button onClick={() => setDeleteTarget(null)} className="cerb-btn-secondary">Cancel</button>
                            <button onClick={confirmDelete} className="bg-red-500 text-snow hover:bg-red-600 px-6 py-2 font-bold uppercase tracking-[0.2em] text-xs" data-testid="gate-delete-confirm-btn">Delete</button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

function Modal({ title, children, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-jet/80 backdrop-blur-sm p-4" onClick={onClose}>
            <div className="relative w-full max-w-2xl bg-jet-elev1 border border-hairline max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-hairline px-5 py-3">
                    <div>
                        <div className="overline">Action</div>
                        <div className="heading-display text-lg">{title}</div>
                    </div>
                    <button onClick={onClose} className="text-snow-dim hover:text-signal"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5">{children}</div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return <div><label className="overline block mb-2">{label}</label>{children}</div>;
}

function Th({ children, className = "" }) {
    return <th className={`text-left px-5 py-3 overline font-normal ${className}`}>{children}</th>;
}
function Td({ children }) {
    return <td className="px-5 py-3 align-middle">{children}</td>;
}
